import { AuthProvider } from '@/contexts/AuthProvider';
import { NavigationProvider } from '@/contexts/NavigationContext';
import { UsersProvider } from '@/contexts/UsersContext';
import { useAuth } from '@/hooks/useAuth';
import { getExplicitRole } from '@/lib/permissions';
import type { ReactNode } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { NoCrmAccess } from './auth/NoCrmAccess';
import { FeedbackWidget } from './feedback/FeedbackWidget';
import DashboardLayout from './page/DashboardLayout';

/**
 * Composant Layout principal qui gère :
 * - Tous les providers/contexts
 * - La logique de layout conditionnelle
 * - L'état global de l'application
 */

interface LayoutProps {
  children: ReactNode;
}

export function LayoutContent({ children }: LayoutProps) {
  const { user, signOut } = useAuth();
  const location = useLocation();

  // Pages qui n'ont pas besoin du DashboardLayout (pages d'auth, pages publiques, etc.)
  const pageUrlsWithoutDashboard = [
    '/login',
    '/forgot-password',
    '/reset-password',
    '/auth/callback',
  ];

  // Vérifier si c'est une page publique (commence par /public/)
  const isPublicPage = location.pathname.startsWith('/public/');

  const shouldShowDashboard =
    user && !isPublicPage && !pageUrlsWithoutDashboard.includes(location.pathname);

  // TICKET-SEC-07 : sans rôle CRM explicite, la base ne donne accès à rien
  // (00308) ; message clair plutôt qu'un tableau de bord vide ou en erreur.
  if (shouldShowDashboard && !getExplicitRole(user.app_metadata?.role)) {
    return <NoCrmAccess onSignOut={signOut} />;
  }

  return shouldShowDashboard ? (
    <>
      <DashboardLayout>{children}</DashboardLayout>
      <FeedbackWidget />
    </>
  ) : (
    <div className="min-h-screen bg-white">{children}</div>
  );
}

export function Layout({ children }: LayoutProps) {
  return (
    <BrowserRouter>
      <AuthProvider>
        <UsersProvider>
          <NavigationProvider>
            <LayoutContent>{children}</LayoutContent>
          </NavigationProvider>
        </UsersProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
